<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useScheduleItems } from '@/composables/useScheduleItems'
import { useEditions } from '@/composables/useEditions'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import type { ScheduleItem, ScheduleItemFormData, ScheduleItemType } from '@/types/scheduleItems'

const SCHEDULE_ITEM_TYPES: ScheduleItemType[] = [
  'general',
  'briefing',
  'race_start',
  'race_finish',
  'bib_pickup',
  'ceremony',
  'side_activity',
]

const { t } = useI18n()
const route = useRoute()

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

const { edition, fetchEdition } = useEditions()
const { items, loading, saving, errors, generalError, fetchScheduleItems, createScheduleItem, updateScheduleItem, deleteScheduleItem } =
  useScheduleItems()

// Modal state
const showModal = ref(false)
const editingItem = ref<ScheduleItem | null>(null)
const showAdvanced = ref(false)

// Delete confirmation state
const deletingItemId = ref<number | null>(null)
const showDeleteConfirm = ref(false)

// Form state
const formData = ref<ScheduleItemFormData>({
  title: '',
  description: null,
  start_datetime: '',
  end_datetime: null,
  location: null,
  type: 'general',
  sort_order: 0,
})
const hasEndTime = ref(false)

const isEditMode = computed(() => editingItem.value !== null)

function formatTime(isoString: string): string {
  // Extract HH:mm directly from ISO string without timezone conversion
  // ISO format: "YYYY-MM-DDTHH:MM:SS..." - we want characters 11-15 (HH:MM)
  return isoString.slice(11, 16)
}

function formatTimeRange(item: ScheduleItem): string {
  const start = formatTime(item.start_datetime)
  if (!item.end_datetime) return start

  // Extract time and date directly from ISO strings without timezone conversion
  const end = item.end_datetime.slice(11, 16)

  const startDay = item.start_datetime.slice(0, 10)
  const endDay = item.end_datetime.slice(0, 10)

  if (startDay !== endDay) {
    return `${start} – ${end} (next day)`
  }
  return `${start} – ${end}`
}

// Convert ISO string to datetime-local value (strip timezone)
function isoToDatetimeLocal(iso: string): string {
  if (!iso) return ''
  // e.g. "2025-04-15T09:00:00+00:00" → "2025-04-15T09:00"
  return iso.slice(0, 16)
}

// Convert datetime-local value to ISO string (treat as-is without offset)
function datetimeLocalToIso(local: string): string {
  if (!local) return ''
  return local.length === 16 ? `${local}:00` : local
}

function defaultStartDatetime(): string {
  if (edition.value?.start_date) {
    return `${edition.value.start_date}T09:00`
  }
  const today = new Date().toISOString().slice(0, 10)
  return `${today}T09:00`
}

function openCreateModal(): void {
  editingItem.value = null
  hasEndTime.value = false
  showAdvanced.value = false
  formData.value = {
    title: '',
    description: null,
    start_datetime: defaultStartDatetime(),
    end_datetime: null,
    location: null,
    type: 'general',
    sort_order: 0,
  }
  showModal.value = true
}

function openEditModal(item: ScheduleItem): void {
  editingItem.value = item
  hasEndTime.value = item.end_datetime !== null
  showAdvanced.value = false
  formData.value = {
    title: item.title,
    description: item.description,
    start_datetime: isoToDatetimeLocal(item.start_datetime),
    end_datetime: item.end_datetime ? isoToDatetimeLocal(item.end_datetime) : null,
    location: item.location,
    type: item.type,
    sort_order: item.sort_order,
  }
  showModal.value = true
}

function closeModal(): void {
  if (saving.value) return
  showModal.value = false
  editingItem.value = null
}


function handleEndTimeToggle(): void {
  if (!hasEndTime.value) {
    formData.value = { ...formData.value, end_datetime: null }
  }
}

async function handleSubmit(): Promise<void> {
  const payload: ScheduleItemFormData = {
    ...formData.value,
    start_datetime: datetimeLocalToIso(formData.value.start_datetime),
    end_datetime: hasEndTime.value && formData.value.end_datetime
      ? datetimeLocalToIso(formData.value.end_datetime)
      : null,
    description: formData.value.description || null,
    location: formData.value.location || null,
  }

  if (!edition.value) return
  const editionId = edition.value.id
  if (isEditMode.value && editingItem.value) {
    const result = await updateScheduleItem(eventSlug, editionId, editingItem.value.id, payload)
    if (result !== null) {
      closeModal()
    }
  } else {
    const result = await createScheduleItem(eventSlug, editionId, payload)
    if (result !== null) {
      closeModal()
    }
  }
}

function requestDelete(item: ScheduleItem): void {
  deletingItemId.value = item.id
  showDeleteConfirm.value = true
}

function cancelDelete(): void {
  deletingItemId.value = null
  showDeleteConfirm.value = false
}

async function confirmDelete(): Promise<void> {
  if (deletingItemId.value === null) return
  if (!edition.value) return
  await deleteScheduleItem(eventSlug, edition.value.id, deletingItemId.value)
  showDeleteConfirm.value = false
  deletingItemId.value = null
}

function fieldError(field: string): string | null {
  const errs = errors.value[field]
  return errs && errs.length > 0 ? (errs[0] ?? null) : null
}

onMounted(async () => {
  await fetchEdition(eventSlug, editionSlug)
  if (edition.value) {
    await fetchScheduleItems(eventSlug, edition.value.id)
  }
})
</script>

<template>
  <div>
    <!-- General error banner -->
    <div
      v-if="generalError"
      class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
    >
      {{ t(generalError) }}
    </div>

    <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
      <!-- Panel header -->
      <div class="flex items-start justify-between mb-2">
        <div>
          <h2 class="text-xl font-bold text-white">{{ t('editions.schedule.heading') }}</h2>
          <p class="text-sm text-slate-400 mt-1">{{ t('editions.schedule.intro') }}</p>
        </div>
        <HeaderCreateButton
          class="shrink-0 ml-4"
          compact
          tight-gap
          :label="t('editions.schedule.add_button')"
          @click="openCreateModal"
        />
      </div>

      <div class="border-t border-slate-700 mt-6 pt-6">
        <!-- Loading state -->
        <div v-if="loading" class="flex items-center justify-center py-8">
          <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="items.length === 0"
          class="rounded-xl border border-dashed border-slate-700 px-6 py-12 text-center"
        >
          <svg
            class="mx-auto mb-4 h-10 w-10 text-slate-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p class="text-base font-semibold text-slate-300 mb-1">{{ t('editions.schedule.empty_title') }}</p>
          <p class="text-sm text-slate-500 mb-4">{{ t('editions.schedule.empty_body') }}</p>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="openCreateModal"
          >
            {{ t('editions.schedule.empty_cta') }}
          </button>
        </div>

        <!-- Schedule items list -->
        <div v-else class="space-y-3">
          <div
            v-for="item in items"
            :key="item.id"
            class="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-slate-800 border border-slate-700 px-4 py-4"
          >
            <!-- Time -->
            <div class="shrink-0 w-32 text-sm font-mono text-emerald-400">
              {{ formatTimeRange(item) }}
            </div>

            <!-- Type badge + title + location -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-0.5">
                <span class="inline-block rounded-full bg-slate-700 px-2 py-0.5 text-xs font-semibold text-slate-300">
                  {{ t(`editions.schedule.types.${item.type}`) }}
                </span>
              </div>
              <p class="text-sm font-semibold text-white truncate">{{ item.title }}</p>
              <p v-if="item.location" class="text-xs text-slate-400 mt-0.5">{{ item.location }}</p>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="text-xs font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-1.5 transition-colors"
                @click="openEditModal(item)"
              >
                {{ t('editions.schedule.edit') }}
              </button>
              <button
                type="button"
                class="text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg px-3 py-1.5 transition-colors"
                @click="requestDelete(item)"
              >
                {{ t('editions.schedule.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit Slide-over ── -->
    <SlideOver
      :open="showModal"
      :title="isEditMode ? t('editions.schedule.form.edit_title') : t('editions.schedule.form.create_title')"
      :saving="saving"
      @close="closeModal"
    >
      <form id="schedule-item-form" novalidate class="space-y-4" @submit.prevent="handleSubmit">
        <!-- General save error -->
        <div
          v-if="generalError && !loading"
          class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        >
          {{ t(generalError) }}
        </div>

        <!-- Title -->
        <div>
          <label for="si-title" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.schedule.form.title_label') }}
          </label>
          <input
            id="si-title"
            v-model="formData.title"
            type="text"
            :placeholder="t('editions.schedule.form.title_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('title') ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="fieldError('title')" class="mt-1 text-xs text-red-400">{{ fieldError('title') }}</p>
        </div>

        <!-- Type -->
        <div>
          <label for="si-type" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.schedule.form.type_label') }}
          </label>
          <select
            id="si-type"
            v-model="formData.type"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('type') ? 'border-red-500' : 'border-slate-600'"
          >
            <option v-for="typeVal in SCHEDULE_ITEM_TYPES" :key="typeVal" :value="typeVal">
              {{ t(`editions.schedule.types.${typeVal}`) }}
            </option>
          </select>
          <p v-if="fieldError('type')" class="mt-1 text-xs text-red-400">{{ fieldError('type') }}</p>
        </div>

        <!-- Start datetime -->
        <div>
          <label for="si-start" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.schedule.form.start_label') }}
          </label>
          <input
            id="si-start"
            v-model="formData.start_datetime"
            type="datetime-local"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('start_datetime') ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="fieldError('start_datetime')" class="mt-1 text-xs text-red-400">{{ fieldError('start_datetime') }}</p>
        </div>

        <!-- End time toggle -->
        <div>
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              v-model="hasEndTime"
              type="checkbox"
              class="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
              @change="handleEndTimeToggle"
            />
            <span class="text-sm text-slate-300">{{ t('editions.schedule.form.end_toggle_label') }}</span>
          </label>
        </div>

        <!-- End datetime (conditional) -->
        <div v-if="hasEndTime">
          <label for="si-end" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.schedule.form.end_label') }}
          </label>
          <input
            id="si-end"
            v-model="formData.end_datetime"
            type="datetime-local"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('end_datetime') ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="fieldError('end_datetime')" class="mt-1 text-xs text-red-400">{{ fieldError('end_datetime') }}</p>
        </div>

        <!-- Location -->
        <div>
          <label for="si-location" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.schedule.form.location_label') }}
          </label>
          <input
            id="si-location"
            v-model="formData.location"
            type="text"
            :placeholder="t('editions.schedule.form.location_placeholder')"
            class="w-full rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <!-- Description -->
        <div>
          <label for="si-description" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.schedule.form.description_label') }}
          </label>
          <textarea
            id="si-description"
            v-model="formData.description"
            rows="3"
            :placeholder="t('editions.schedule.form.description_placeholder')"
            class="w-full rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
        </div>

        <!-- Advanced settings toggle -->
        <div>
          <button
            type="button"
            class="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-300 transition-colors"
            @click="showAdvanced = !showAdvanced"
          >
            <svg
              class="w-3.5 h-3.5 transition-transform"
              :class="showAdvanced ? 'rotate-90' : ''"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
            {{ t('editions.schedule.form.advanced') }}
          </button>

          <div v-if="showAdvanced" class="mt-3">
            <label for="si-sort-order" class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('editions.schedule.form.sort_order_label') }}
            </label>
            <input
              id="si-sort-order"
              v-model.number="formData.sort_order"
              type="number"
              min="0"
              max="10000"
              class="w-full sm:w-32 rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p class="mt-1 text-xs text-slate-500">{{ t('editions.schedule.form.sort_order_hint') }}</p>
            <p v-if="fieldError('sort_order')" class="mt-1 text-xs text-red-400">{{ fieldError('sort_order') }}</p>
          </div>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            :disabled="saving"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
            @click="closeModal"
          >
            {{ t('editions.schedule.form.cancel') }}
          </button>
          <button
            type="submit"
            form="schedule-item-form"
            :disabled="saving"
            class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          >
            <AppSpinner v-if="saving" size="button" />
            {{ isEditMode ? t('editions.schedule.form.submit_edit') : t('editions.schedule.form.submit_create') }}
          </button>
        </div>
      </template>
    </SlideOver>

    <!-- ── Delete Confirmation Dialog ── -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/70"
        @click="cancelDelete"
      />

      <!-- Dialog -->
      <div class="relative z-10 w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
        <h3 class="text-base font-bold text-white mb-2">
          {{ t('editions.schedule.delete_confirm_title') }}
        </h3>
        <p class="text-sm text-slate-400 mb-5">
          {{ t('editions.schedule.delete_confirm_body') }}
        </p>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="text-sm font-medium text-slate-400 hover:text-white transition-colors px-4 py-2"
            @click="cancelDelete"
          >
            {{ t('editions.schedule.delete_confirm_cancel') }}
          </button>
          <button
            type="button"
            :disabled="saving"
            class="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="confirmDelete"
          >
            <svg
              v-if="saving"
              class="animate-spin h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ t('editions.schedule.delete_confirm_action') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
